import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';
import { translate } from '../i18n/config';
import { useQuestionnaire, SurveyAnswerForRecommendations } from '../context/QuestionnaireContext';
import { surveyApi } from '../services/api';
import backgroundImage from '../assets/img/hj494nf.jpeg';
import './QuestionnairePage.css';

interface QuestionOption {
  id: string;
  optionText: string;
  optionValue: string;
  displayOrder: number;
}

interface Question {
  id: string;
  questionText: string;
  questionType: 'SingleChoice' | 'MultipleChoice';
  isRequired: boolean;
  displayOrder: number;
  metadata: string;
  options: QuestionOption[];
}

export function getSurveyLocale(i18nLanguage: string): string {
  const base = (i18nLanguage || 'en').split('-')[0];
  if (base === 'tr') return 'tr-TR';
  if (base === 'de') return 'de-DE';
  if (base === 'es') return 'es-ES';
  if (base === 'fr') return 'fr-FR';
  return 'en-US';
}

function localizeSurveyQuestions(
  sortedQuestions: any[],
  surveyLocale: string,
  t: (key: string, opts?: Record<string, string | number>) => string
): Question[] {
  return sortedQuestions.map((q: any) => {
    const qTranslation = (q.translations || []).find(
      (tr: any) => tr.locale === surveyLocale
    );

    const localizedQuestionText: string =
      (qTranslation && qTranslation.questionText) || q.questionText || '';

    let localizedMetadata: string = q.metadata || '';
    if (localizedMetadata && /En\s*fazla|Maximum/i.test(localizedMetadata)) {
      const match =
        localizedMetadata.match(/En\s*fazla\s*(\d+)/i) ??
        localizedMetadata.match(/Maximum\s*(\d+)/i);
      if (match) {
        localizedMetadata = t('questionnaire.metadataMax', { count: match[1] });
      }
    }

    const localizedOptions: QuestionOption[] = (q.options || []).map((opt: any) => {
      const optTranslation = (opt.translations || []).find(
        (tr: any) => tr.locale === surveyLocale
      );
      return {
        id: opt.id,
        optionText: (optTranslation && optTranslation.optionText) || opt.optionText || '',
        optionValue: opt.optionValue,
        displayOrder: opt.displayOrder,
      };
    });

    return {
      id: q.id,
      questionText: localizedQuestionText,
      questionType: q.questionType,
      isRequired: q.isRequired,
      displayOrder: q.displayOrder,
      metadata: localizedMetadata,
      options: localizedOptions,
    };
  });
}

function getClientPayload(): {
  platform: string;
  appVersion: string;
  timezone: string;
  locale: string;
} {
  const timezone =
    typeof Intl !== 'undefined' && Intl.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : '';
  return {
    platform: typeof navigator !== 'undefined' ? 'web' : 'unknown',
    appVersion: process.env.REACT_APP_VERSION || '1.0.0',
    timezone,
    locale: typeof navigator !== 'undefined' ? navigator.language || '' : '',
  };
}

function getLocationPayload(): Promise<{
  latitude: number;
  longitude: number;
  accuracyMeters: number;
  provider: string;
  capturedAt: string;
} | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(null), 8000);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeout);
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMeters: position.coords.accuracy ?? 0,
          provider: 'browser',
          capturedAt: new Date().toISOString(),
        });
      },
      () => {
        clearTimeout(timeout);
        resolve(null);
      },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 300000 }
    );
  });
}

/** Parse max allowed selections from question metadata (e.g. "En fazla 3" or "Maximum 3 options"). */
function getMaxSelectionsFromMetadata(metadata: string | undefined): number {
  if (!metadata || typeof metadata !== 'string') return Infinity;
  const trimmed = metadata.trim();
  // Turkish: "En fazla 3 seçenek ..." or "(En fazla 3 seçenek ...)"
  const tr = trimmed.match(/En\s*fazla\s*(\d+)/i);
  if (tr) return parseInt(tr[1], 10);
  // English: "Maximum 3 options" or "(Maximum 3 options can be selected.)"
  const en = trimmed.match(/Maximum\s*(\d+)/i) ?? trimmed.match(/(\d+)\s*options?/i);
  if (en) return parseInt(en[1], 10);
  // Fallback: any "N seçenek" or "N option"
  const fallback = trimmed.match(/(\d+)\s*(?:seçenek|option)/i);
  if (fallback) return parseInt(fallback[1], 10);
  return Infinity;
}

const QuestionnairePage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { answers, setAnswers, responseId, setResponseId, setSurveyAnswersForRecommendations } = useQuestionnaire();
  const [currentStep, setCurrentStep] = useState(1);
  const [rawSurveyQuestions, setRawSurveyQuestions] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const questions = useMemo(() => {
    if (!rawSurveyQuestions || rawSurveyQuestions.length === 0) return [];
    const surveyLocale = getSurveyLocale(i18n.language);
    return localizeSurveyQuestions(rawSurveyQuestions, surveyLocale, t);
  }, [rawSurveyQuestions, i18n.language, t]);

  const totalSteps = questions.length;

  // Fetch survey questions on component mount
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        setLoadingProgress(0);
        
        // Simulate progress: Fetching surveys (20%)
        setLoadingProgress(20);
        const surveys = await surveyApi.getSurveys();
        
        // Get the first active survey
        const activeSurvey = surveys.find((s: any) => s.status === 'Active') || surveys[0];
        
        if (!activeSurvey) {
          throw new Error('NO_ACTIVE_SURVEY');
        }

        // Simulate progress: Creating response (40%)
        setLoadingProgress(40);
        // Create a new survey response for this user and survey
        const response = await surveyApi.createSurveyResponse(activeSurvey.id);
        // Assume API returns an object that includes the response id as `id`
        if (response?.id) {
          setResponseId(response.id);
        } else if (response?.responseId) {
          setResponseId(response.responseId);
        } else {
          console.warn('Survey response created but no id field found in response:', response);
        }
        
        // Simulate progress: Fetching survey details (60%)
        setLoadingProgress(60);
        // Get survey details with questions
        const surveyDetails = await surveyApi.getSurveyDetails(activeSurvey.id);
        
        // Simulate progress: Processing questions (80%)
        setLoadingProgress(80);
        // Sort questions by displayOrder
        const sortedQuestions = surveyDetails.questions.sort((a: Question, b: Question) => 
          a.displayOrder - b.displayOrder
        );

        // Simulate progress: Complete (100%)
        setLoadingProgress(100);

        setRawSurveyQuestions(sortedQuestions);
      } catch (err: any) {
        console.error('Error fetching questions:', err);
        if (err?.message === 'NO_ACTIVE_SURVEY') {
          setError(translate('questionnaire.noSurvey'));
        } else {
          setError(err.message || translate('questionnaire.fetchError'));
        }
      } finally {
        setLoading(false);
        setLoadingProgress(0);
      }
    };

    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOptionSelect = (optionValue: string) => {
    const currentQuestion = questions[currentStep - 1];
    
    if (currentQuestion.questionType === 'MultipleChoice') {
      // Handle multiple choice - allow multiple selections
      setAnswers((prev) => {
        const prevAnswer = prev[currentQuestion.id as keyof typeof prev];
        const currentAnswers = Array.isArray(prevAnswer) ? prevAnswer : [];
        const isSelected = currentAnswers.includes(optionValue);
        
        if (isSelected) {
          // Deselect if already selected
          return {
            ...prev,
            [currentQuestion.id]: currentAnswers.filter((val) => val !== optionValue),
          };
        } else {
          // Select (enforce max from metadata, e.g. "En fazla 3" or "Maximum 3 options")
          const maxCount = getMaxSelectionsFromMetadata(currentQuestion.metadata);
          if (currentAnswers.length < maxCount) {
            return {
              ...prev,
              [currentQuestion.id]: [...currentAnswers, optionValue],
            };
          }
          return prev;
        }
      });
    } else {
      // Handle single choice
      setAnswers((prev) => ({
        ...prev,
        [currentQuestion.id]: optionValue,
      }));
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
    } else {
      // All steps completed - store answers and prepare for backend submission
      console.log('All steps completed, calling handleSubmit');
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    } else {
      navigate('/start-analysis');
    }
  };

  const handleSubmit = async () => {
    console.log('handleSubmit called');
    try {
      // Build answers payload using questions and stored answers
      const answerDate = new Date().toISOString();
      const aggregatedAnswers: {
        questionId: string;
        optionId: string | null;
        answerText: string | null;
        answerNumber: number | null;
        answerDate: string | null;
        answerRating: number | null;
      }[] = [];
      const surveyAnswersForRecommendations: SurveyAnswerForRecommendations[] = [];

      questions.forEach((question) => {
        const rawAnswer = answers[question.id];
        if (rawAnswer === null || rawAnswer === undefined) {
          return;
        }

        const pushAnswer = (value: string) => {
          const matchedOption = question.options.find(
            (opt) => opt.optionValue === value || opt.optionText === value
          );

          aggregatedAnswers.push({
            questionId: question.id,
            optionId: matchedOption ? matchedOption.id : null,
            answerText: matchedOption ? matchedOption.optionText : value,
            answerNumber: null,
            answerDate,
            answerRating: null,
          });
        };

        const asArray = Array.isArray(rawAnswer) ? rawAnswer : [rawAnswer as string];

        // For survey submission
        asArray.forEach((val) => pushAnswer(val));

        // For recommendations API – collect optionIds per question
        const optionIds = asArray
          .map((val) => {
            const matchedOption = question.options.find(
              (opt) => opt.optionValue === val || opt.optionText === val
            );
            return matchedOption?.id;
          })
          .filter((id): id is string => Boolean(id));

        if (optionIds.length > 0) {
          surveyAnswersForRecommendations.push({
            questionId: question.id,
            optionIds,
            answerNumber: null,
          });
        }
      });

      if (responseId && aggregatedAnswers.length > 0) {
        const client = getClientPayload();
        const location = await getLocationPayload();
        await surveyApi.submitSurveyAnswers(responseId, {
          client,
          location,
          answers: aggregatedAnswers,
        });
      } else if (!responseId) {
        console.warn('No responseId available when trying to submit answers.');
      }

      // Store compact survey answers for later recommendations call
      setSurveyAnswersForRecommendations(surveyAnswersForRecommendations);

      console.log('Questionnaire Answers saved to context:', answers);
      
      // Navigate to GDPR consent page
      navigate('/gdpr-consent');
    } catch (err) {
      console.error('Failed to submit survey answers:', err);
      // Still navigate to GDPR consent page even if submission fails
      navigate('/gdpr-consent');
    }
  };

  if (loading) {
    return (
      <div className="questionnaire-page loading-fullpage">
        <div className="loading-container">
          <p className="loading-text">{t('questionnaire.loading')}</p>
          <div className="loading-progress">
            <div className="loading-progress-bar">
              <div 
                className="loading-progress-fill"
                style={{ width: `${loadingProgress}%` }}
              />
            </div>
            <span className="loading-progress-text">{loadingProgress}%</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || questions.length === 0) {
    return (
      <div className="questionnaire-page">
        <div className="questionnaire-wrapper">
          <div className="error-container">
            <p>
              {t('questionnaire.errorPrefix')} {error || t('questionnaire.noQuestions')}
            </p>
            <button onClick={() => navigate('/start-analysis')}>{t('questionnaire.goBack')}</button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentStep - 1];
  const currentAnswer = answers[currentQuestion.id as keyof typeof answers];
  
  // Check if can proceed based on question type
  const canProceed = currentQuestion.questionType === 'MultipleChoice'
    ? Array.isArray(currentAnswer) && (currentAnswer as string[]).length > 0
    : currentAnswer !== null && currentAnswer !== undefined;

  return (
    <div className="questionnaire-page">
      <div className="background-image-container">
        <img src={backgroundImage} alt={t('questionnaire.backgroundAlt')} className="background-image" />
      </div>
      <div className="questionnaire-wrapper">
        <div className="questionnaire-header">
        <button className="back-arrow-button" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <span className="step-indicator">{t('questionnaire.step', { current: currentStep })}</span>
      </div>

      <div className="progress-bar-container">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      <div className="questionnaire-content">
        <div className="question-section">
          <h2 className="question-text">{currentQuestion.questionText}</h2>
          {currentQuestion.metadata && (
            <p className="question-metadata">{currentQuestion.metadata}</p>
          )}
        </div>

        <div className="options-section">
          {currentQuestion.options
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((option) => {
              const isSelected = currentQuestion.questionType === 'MultipleChoice'
                ? Array.isArray(currentAnswer) && (currentAnswer as string[]).includes(option.optionValue)
                : currentAnswer === option.optionValue;
              
              return (
                <button
                  key={option.id}
                  className={`option-button ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleOptionSelect(option.optionValue)}
                >
                  {isSelected && (
                    <svg 
                      className="checkmark-icon" 
                      width="20" 
                      height="20" 
                      viewBox="0 0 20 20" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path 
                        d="M16.6667 5L7.50004 14.1667L3.33337 10" 
                        stroke="white" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  <span>{option.optionText}</span>
                </button>
              );
            })}
        </div>

        <div className="navigation-section">
          <button className="nav-button" onClick={handleBack}>
            {t('questionnaire.back')}
          </button>
          <button 
            className="nav-button next-button" 
            onClick={handleNext}
            disabled={!canProceed}
          >
            {currentStep === totalSteps ? t('questionnaire.complete') : t('questionnaire.next')}
          </button>
        </div>

        <div className="step-dots">
          {Array.from({ length: totalSteps }).map((_, index) => (
            <div
              key={index}
              className={`step-dot ${index + 1 === currentStep ? 'active' : ''}`}
            />
          ))}
        </div>
      </div>
      </div>
    </div>
  );
};

export default QuestionnairePage;
